/**
 * Step 2 & 4 — Character/format rules and reserved/impersonation terms.
 * Names: letters, spaces, hyphen, apostrophe. Block emojis, numbers, symbols.
 * Uses config for limits and reserved list.
 */

import { normalizeForValidation } from './normalizeText.js';

/**
 * @param {string} text
 * @param {'name'|'businessName'} context
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} config
 * @returns {{ valid: boolean, errorCode?: string, message?: string }}
 */
export function checkTextRules(text, context, config) {
  const trimmed = (text || '').trim();
  if (!trimmed.length) {
    return { valid: true };
  }

  const limits = context === 'businessName' ? config.limits.businessName : config.limits.name;
  if (trimmed.length < limits.min) {
    return {
      valid: false,
      errorCode: 'TOO_SHORT',
      message: config.messages.tooShort,
    };
  }
  if (trimmed.length > limits.max) {
    return {
      valid: false,
      errorCode: 'TOO_LONG',
      message: config.messages.tooLong,
    };
  }

  // Names: only letters (any language), spaces, hyphen, apostrophe
  const nameRegex = config.nameAllowedRegex;
  if (!nameRegex.test(trimmed)) {
    return {
      valid: false,
      errorCode: 'NAME_INVALID_CHARS',
      message: config.messages.nameInvalidChars,
    };
  }

  // Reserved terms (case-insensitive)
  const lower = trimmed.toLowerCase();
  const reserved = [...(config.reservedTerms || []), ...(config.platformNameVariations || [])];
  for (const term of reserved) {
    if (lower === term || lower.includes(term)) {
      return {
        valid: false,
        errorCode: 'RESERVED_TERM',
        message: config.messages.reservedTerm,
      };
    }
  }

  return { valid: true };
}

/**
 * Check reserved terms only (for non-name contexts: description, message, review, etc.)
 * @param {string} text
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} config
 */
export function checkReservedTerms(text, config) {
  const normalized = normalizeForValidation(text, { lowercase: true });
  if (!normalized.length) return { valid: true };

  const reserved = [...(config.reservedTerms || []), ...(config.platformNameVariations || [])];
  for (const term of reserved) {
    if (normalized.includes(term)) {
      return {
        valid: false,
        errorCode: 'RESERVED_TERM',
        message: config.messages.reservedTerm,
      };
    }
  }
  return { valid: true };
}

/**
 * Step 2 (general text): Block emojis and excessive symbols in messages/reviews/forms.
 * Spec: "Allow broader punctuation" but "Still block excessive symbols and emojis".
 * @param {string} text - Normalized text
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig} config
 * @returns {{ valid: boolean, errorCode?: string, message?: string }}
 */
export function checkEmojiAndExcessiveSymbols(text, config) {
  if (!text || !text.length) return { valid: true };
  const messages = config.messages || {};
  const blockEmoji = config.blockEmoji !== false;
  const maxSymbolRatio = config.maxSymbolRatio ?? 0.4;

  // Block emoji (Unicode Emoji property)
  if (blockEmoji && /\p{Emoji}/u.test(text)) {
    return { valid: false, errorCode: 'EMOJI_OR_SYMBOLS', message: messages.emojiOrSymbols ?? 'Please remove emojis or excessive symbols.' };
  }

  // Excessive symbols: of non-space chars, more than maxSymbolRatio should not be symbols/numbers
  const withoutSpaces = text.replace(/\s/g, '');
  if (withoutSpaces.length < 5) return { valid: true };
  const allowedBasic = (withoutSpaces.match(/[\p{L}\d.,!?'\-]/gu) || []).length;
  const symbolRatio = 1 - (allowedBasic / withoutSpaces.length);
  if (symbolRatio > maxSymbolRatio) {
    return { valid: false, errorCode: 'EMOJI_OR_SYMBOLS', message: messages.emojiOrSymbols ?? 'Please remove emojis or excessive symbols.' };
  }

  return { valid: true };
}
